import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Star } from "lucide-react";
import { cassa, money } from "./cassaApi";

const gross = (l, incl) => (+l.price || 0) * (incl ? 1 : 1 + (+l.vat || 0) / 100);
const needFor = (l, incl, ppe) => l.points_required ?? Math.ceil(gross(l, incl) * ppe - 1e-6);

export function PointsBox({ draft, setDraft, earned = 0 }) {
  const [pts, setPts] = useState(null);
  const [val, setVal] = useState("");
  const [bk, setBk] = useState("");
  const cid = draft.client?.id;
  useEffect(() => { setPts(null); if (cid) cassa.get(`/clients/${cid}/points`).then((r) => setPts(r.data)).catch(() => {}); }, [cid]);
  if (!cid || !pts || !pts.enabled) return null;
  const ppe = pts.points_per_euro;
  const used = draft.lines.reduce((a, l) => a + (l.points || 0), 0);
  const avail = pts.balance - used;
    const eligible = draft.lines.map((l, i) => ({ l, i })).filter(({ l }) => !l.points && +l.price > 0 && needFor(l, draft.include_vat, ppe) <= avail);
  const bookingCredit = async () => {
    try {
      const { data } = await cassa.get(`/bookings/${encodeURIComponent(bk.trim().toUpperCase())}/credit`, { params: { client_id: cid } });
      const price = draft.include_vat ? -data.discount : -(data.discount / 1.22);
      setDraft((d) => ({ ...d, booking_number: data.number, lines: [...d.lines.filter((l) => !l.booking), { key: Date.now(), code: "", description: `Sconto punti già usati - prenotazione ${data.number}`, qty: 1, price: +price.toFixed(4), discount: "", vat: 22, natura: "", booking: true }] }));
      setBk(""); toast.success(`Sconto prenotazione ${data.number} applicato`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Prenotazione non trovata"); }
  };
  const discount = () => {
    const p = Math.floor((+val || 0) / ppe) * ppe;
    if (p <= 0) return toast.error(`Inserisci un multiplo di ${ppe} punti`);
    if (p > avail) return toast.error("Punti insufficienti");
    const eur = p / ppe;
    const price = draft.include_vat ? -eur : -(eur / 1.22);
    setDraft((d) => ({ ...d, lines: [...d.lines, { key: Date.now(), code: "", description: `Sconto punti fedeltà ${pts.card_id} (${p} punti)`, qty: 1, price: +price.toFixed(4), discount: "", vat: 22, natura: "", points: p }] }));
    setVal("");
  };
  const gift = ({ l, i }) => {
    const need = needFor(l, draft.include_vat, ppe);
    if (need > avail) return toast.error(`Servono ${need} punti`);
    setDraft((d) => {
      const lines = [...d.lines];
      const one = { ...l, key: Date.now(), qty: 1, discount: 100, points: need, description: `${l.description} (omaggio punti)` };
      if ((+l.qty || 1) > 1) lines.splice(i, 1, { ...l, qty: +l.qty - 1 }, one); else lines.splice(i, 1, one);
      return { ...d, lines };
    });
  };
  return (
    <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-3 space-y-2 text-sm" data-testid="cassa-points-box">
      <div className="flex flex-wrap items-center gap-3">
        <Star className="w-4 h-4 text-amber-600" /><b className="font-mono" data-testid="cassa-points-card">{pts.card_id}</b>
        <span>Saldo <b data-testid="cassa-points-balance">{pts.balance}</b> punti ({money(pts.balance / ppe)})</span>
        {used > 0 && <span className="text-amber-800" data-testid="cassa-points-used">in uso {used} · restano {avail}</span>}
      </div>
      <div className="rounded bg-white/70 px-2 py-1.5 font-semibold text-amber-900" data-testid="cassa-points-earned">Punti maturati con questa vendita: <b className="text-lg">{earned}</b> <span className="font-normal text-xs">(accreditati all'emissione · nuovo saldo {avail + earned})</span></div>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="cassa-points-input" value={val} onChange={(e) => setVal(e.target.value.replace(/\D/g, ""))} placeholder={`punti (multipli di ${ppe})`} className="h-9 w-40 px-2 rounded border bg-white" />
        <button data-testid="cassa-points-discount" onClick={discount} className="press h-9 px-3 rounded bg-amber-500 text-white font-semibold">Applica sconto</button>
        {eligible.map((x) => (
          <button key={x.l.key} data-testid={`cassa-points-gift-${x.i}`} onClick={() => gift(x)} className="press h-9 px-3 rounded border border-amber-500 bg-white font-semibold flex items-center gap-1">
            <Gift className="w-4 h-4" /> Omaggio {x.l.code} ({needFor(x.l, draft.include_vat, ppe)} punti)
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input data-testid="cassa-booking-credit-input" value={bk} onChange={(e) => setBk(e.target.value)} placeholder="Prenotazione con punti (PR-2026-0001)" className="h-9 w-60 px-2 rounded border bg-white" />
        <button data-testid="cassa-booking-credit-btn" onClick={bookingCredit} disabled={!bk.trim()} className="press h-9 px-3 rounded border border-amber-500 bg-white font-semibold disabled:opacity-50">Sconto prenotazione</button>
        {draft.booking_number && <span className="text-xs text-amber-800" data-testid="cassa-booking-credit-applied">Applicato {draft.booking_number}</span>}
      </div>
      <p className="text-[11px] text-amber-900">1 punto ogni € speso su {pts.categories.join(", ")}. Omaggio = punti premio dell'articolo; sconto: {ppe} punti = 1 €.</p>
    </div>
  );
}
